import * as React from 'react';
import styled from 'styled-components';
import Colours from '../State/Colours';
import Context from '../State/Context';
import devices from '../State/Devices';
import ButtonStartOver from './ButtonStartOver';
import { AngleLeft, AngleRight } from './Icons';

interface InterfaceProgressItem {
    status: 'active'|'complete'|'disabled',
}

const getProgressColour = (props: InterfaceProgressItem) => {
    switch (props.status) {
        case 'complete':
            return Colours.primary;
        case 'disabled':
            return Colours.disabledPanelText;
        case 'active':
        default:
            return Colours.text;
    }
};

const getProgressWeight = (props: InterfaceProgressItem) => {
    switch (props.status) {
        case 'active':
        case 'complete':
            return '600';
        default:
            return 'normal';
    }
};

const isClickable = (status: string) => {
    switch (status) {
        case 'active':
        case 'complete':
            return true;
        default:
            return false;
    }
}

const getCursor = (props: InterfaceProgressItem) => {
    switch (props.status) {
        case 'active':
        case 'complete':
            return 'pointer';
        default:
            return 'normal';
    }
};

const ProgressList = styled.div`
    display: -ms-grid;
    display: grid;
    height: 460px;
    grid-row-gap: 10px;
    -ms-grid-columns: 1fr;

    .mobileProgressPanel {
        display: none;
    }

    @media ${devices.laptop} {
        height: auto;
        grid-row-gap: 4px;
        grid-template-columns: 1fr;
        -ms-grid-columns: 1fr;
        align-items: center;

        .mobileProgressPanel {
            display: -ms-grid;
            display: grid;
            text-align: center;
            grid-template-columns: 30px 1fr 30px;
            -ms-grid-columns: 30px 1fr 30px;
            grid-template-areas: "back currentPage forward" /* TODO */
                                 "back currentPageTitle forward";
            align-items: center;

            .fa-angle-left { grid-area: back; cursor: pointer; }
            .fa-angle-left:hover { color: ${Colours.primary}; }

            .fa-angle-right { grid-area: forward; cursor: pointer; justify-self: end; -ms-grid-column-align: end; }
            .fa-angle-right:hover { color: ${Colours.primary}; }

            span { grid-area: currentPage; }
            strong { grid-area: currentPageTitle; }
            div { grid-area: forward; background-color: red; }
        }

        div:nth-child(1) { display: none; }
        div:nth-child(2) { display: none; }
        div:nth-child(3) { display: none; }
        div:nth-child(4) { display: none; }
        div:nth-child(5) { display: none; }
        div:nth-child(6) { display: none; }
        div:nth-child(7) { display: none; }
    }
`;

const ProgressItem = styled.div`
    font-weight: ${getProgressWeight};
    color: ${getProgressColour};
    cursor: ${getCursor};
`;

const ProgressLocation = styled.div`
    margin-top: 100px;
    padding: 20px 0 20px 0;
    border-top: 2px solid ${Colours.gray};
    display: -ms-grid;
    display: grid;
    grid-row-gap: 10px;
    
    
    span {
        display: block;
        -ms-grid-column: 1;
    }
    
    span:nth-child(1) { -ms-grid-row: 1; }
    span:nth-child(2) { -ms-grid-row: 2; }
`;

const ProgressPanelStyled = styled.div`
    display: -ms-grid;
    display: grid;
    padding: 40px 0 40px 60px;
    grid-template-columns: 1fr;
    -ms-grid-columns: 1fr;
    -ms-grid-column: 1;

    .ms-line1 { -ms-grid-row: 1; margin-bottom: 1em; }
    .ms-line2 { -ms-grid-row: 2; margin-bottom: 1em; }
    .ms-line3 { -ms-grid-row: 3; margin-bottom: 1em; }
    .ms-line4 { -ms-grid-row: 4; margin-bottom: 1em; }
    .ms-line5 { -ms-grid-row: 5; margin-bottom: 1em; }
    .ms-line6 { -ms-grid-row: 6; margin-bottom: 1em; }
    .ms-line7 { -ms-grid-row: 7; margin-bottom: 1em; }
    .ms-line8 { -ms-grid-row: 8; margin-bottom: 1em; }

    background-color: ${Colours.panelBackground};

    @media ${devices.laptop} {
        padding: 8px;
        grid-template-columns: 1fr;
        -ms-grid-columns: 1fr;
    }
`;

const ButtonStartOverContainer = styled.div`
    @media ${devices.laptop} {
        display: none;
    }
`;

export default class ProgressPanel extends React.Component {
    public mobileProgressPanel = (page: number, maxPages: number, onPreviousPage: () => void, onNextPage?: () => void) => {
        const pageTitles = {
            1: 'Property location',
            2: 'Unlicensed water use',
            3: 'Licensed water use',
            4: 'Industrial and commercial',
            5: 'Total water use',
        };

        const title = pageTitles[page];

        return (
            <div className="mobileProgressPanel">
                {page > 1 && <AngleLeft size="2x" onClick={onPreviousPage}/>}
                <span>{page} / {maxPages}</span>
                <strong>{title}</strong>
                {!!onNextPage && (page < maxPages) && <AngleRight size="2x" onClick={onNextPage}/>}
            </div>
        )
    }

    public render() {
        return (
            <ProgressPanelStyled className="ProgressPanel">
                <Context.Consumer>
                {
                    ({ page, maxPages, onGotoPage, onPreviousPage, onNextPage, onReset, messages, userState, isCurrentPageComplete }) => {
                        const onGotoPage1 = () => isClickable(this.status(1, page)) && onGotoPage(1);
                        const onGotoPage2 = () => isClickable(this.status(2, page)) && onGotoPage(2);
                        const onGotoPage3 = () => isClickable(this.status(3, page)) && onGotoPage(3);
                        const onGotoPage4 = () => isClickable(this.status(4, page)) && onGotoPage(4);
                        const onGotoPage5 = () => isClickable(this.status(5, page)) && onGotoPage(5);
                        const { 
                            progressTitlePage1, 
                            progressTitlePage2, 
                            progressTitlePage3, 
                            progressTitlePage4, 
                            progressTitlePage5,
                        } = messages;

                        return (
                            <ProgressList>
                                <ProgressItem className="ms-line1" status={this.status(1, page)} onClick={onGotoPage1}>{progressTitlePage1}</ProgressItem>
                                <ProgressItem className="ms-line2" status={this.status(2, page)} onClick={onGotoPage2}>{progressTitlePage2}</ProgressItem>
                                <ProgressItem className="ms-line3" status={this.status(3, page)} onClick={onGotoPage3}>{progressTitlePage3}</ProgressItem>
                                <ProgressItem className="ms-line4" status={this.status(4, page)} onClick={onGotoPage4}>{progressTitlePage4}</ProgressItem>
                                <ProgressItem className="ms-line5" status={this.status(5, page)} onClick={onGotoPage5}>{progressTitlePage5}</ProgressItem>
                                <ProgressLocation className="ms-line6">
                                    <span>{userState.suburb.value}</span>
                                    <span>{userState.climateZone.value}</span>
                                </ProgressLocation>

                                <ButtonStartOverContainer className="ms-line7">
                                    <ButtonStartOver showTooltip={true} onReset={onReset}/>
                                </ButtonStartOverContainer>
                                {this.mobileProgressPanel(page, maxPages, onPreviousPage, isCurrentPageComplete() ? onNextPage : undefined)}
                            </ProgressList>
                        )
                    }
                }
                </Context.Consumer>
            </ProgressPanelStyled>
        );
    }

    private status = (page:number, activePage: number) => {
        if (page === activePage) {
            return 'active';
        }

        if (page < activePage) {
            return 'complete';
        }

        return 'disabled';
    };
}


// WEBPACK FOOTER //
// ./src/Components/ProgressPanel.tsx